/**
 * Import react libraries.
 */
import React from 'react';

/**
 * Import third-party libraries.
 */
import styled, { css, useTheme } from 'styled-components';
import DatePicker, { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import en from 'date-fns/locale/en-GB';
import dayjs from 'dayjs';
import advancedFormat  from 'dayjs/plugin/advancedFormat';
import quarterOfYear from "dayjs/plugin/quarterOfYear";

dayjs.extend(advancedFormat);
dayjs.extend(quarterOfYear);

/**
 * Import custom components.
 */
import COLORS from '../styles/colors';
import {SystemIcons} from '../icons';
import {DatepickerFieldHeader} from './DatepickerFieldHeader';
import {ComponentTextStyle, focusStyles, scrollBarStyling} from '../styles';
import TooltipOverflow from '../Tooltips/TooltipOverflow';

/**
 * Import custom styles.
 */
import {ErrorMessage, InputFieldStyling, InputWrapper} from './styling';
import '../styles/react-datepicker.css';
import {ComponentMStyling, ComponentSStyling} from '../styles';
import {Z_INDEXES} from '../styles';
import {useFocusVisibleRef} from '../common';
import {Size, Testable} from '../types';

/**
 * Add custom types.
 */
export type DatepickerFieldProps = Testable & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'size' | 'onBlur'> & {
  /** Optional. Selected date. Defaults to current date */
  value?: Date;
  /** Optional. Handler to be called when selected date was changed. */
  onChange?: (date: Date) => void;
  /** Optional. Handler to be called when control is losing focus. */
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  /** Optional. Quarter picker mode. If set, then user can not select a particular date, but quarter of the year.
   * 1st day of the quarter will be set as value.
   */
  showQuarterYearPicker?: boolean;
  /** Optional. If set, then input field will be shown in 'invalid' state */
  invalid?: boolean;
  /** Optional. Format in which date will be shown in the input field. Defaults to 'MMMM Do, yyyy'. 
   * For quarter picker will be shown in 'YYYY, qqq' format.
   */
  dateFormat?: string;
  /** Optional. Validation message to be shown under the input field */
  validationMessage?: string;
  /** Optional. Value for 'autocomplete' attribute of input field tag. */
  autoComplete?: string;
  /** Optional. Placeholder text to be shown when no value is selected. */
  placeholder?: string;
  /** Optional. If set, then user will be able to open year selector by clicking on the month in the dropdown. */
  yearPicker?: boolean;
  /** Optional. How many years before current year will be available in the year selector mode. */
  yearsBeforeCurrentDate?: number;
  /** Optional. How many years after current year will be available in the year selector mode. */
  yearsAfterCurrentDate?: number;
  /** Optional. Margin value to be set to the dropdown container. */
  margin?: string;
  /** Optional. Position where to show tooltip in case of closed datepicker and value overflowing the input field. */
  overflowTooltipPosition?: 'top' | 'bottom';
};


const unselectedHoverColoringText = css`
    color: ${props => COLORS.generateToken({componentType: 'text', state: 'hover'}, props.theme)} !important;
    background: ${props => COLORS.generateToken({componentType: 'bg-surface', state: 'hover'}, props.theme)} !important;  
`;

const selectedDefaultColoringText = css`
    color: ${props => COLORS.generateToken({ componentType: 'text', isOnFill: true, defaultVariant: 'primary' }, props.theme)} !important;
    background: ${props => COLORS.generateToken({ componentType: 'bg-fill', defaultVariant: 'primary' }, props.theme)} !important;
`;

const todayDefaultColoringText = css`
    color: ${props => COLORS.generateToken({ componentType: 'text', defaultVariant: 'default' }, props.theme)};
    background: ${props => COLORS.generateToken({ componentType: 'bg-surface', defaultVariant: 'selected' }, props.theme)};
`;

/**
 * Add custom styles.
 */
const DatePickerContainer = styled.div<{ $yearPicker: boolean; $margin: string; $quarterPicker?: boolean }>`
  .react-datepicker-popper {
    z-index: ${Z_INDEXES.dropdown};
  }

  .react-datepicker-popper[data-placement^=bottom] {
    padding-top: 0px;
  }

  
  .react-datepicker-popper[data-placement^=top] {
    padding-bottom: 0px;
  }

  > div {
    display: block;

    ${(props) => (props.$margin ? `margin: ${props.$margin};` : '')}
    .react-datepicker {
      box-sizing: border-box;
      box-shadow: 0px 2px 4px rgb(0 0 0 / 15%);
      border: 1px solid #e5e5e5;
    }
    .react-datepicker__input-container{
      z-index: 0;
    }

    .react-datepicker__navigation {
      line-height: normal;
      text-indent: inherit;
      border: none;
      border-radius: 4px;
      height: 48px;
      width: 48px;
      outline: none;
      margin: 0px;
      color: transparent;

      &:hover {
        background: ${props => COLORS.generateToken({componentType:'bg-surface', state:'hover'}, props.theme)};

        &.react-datepicker__navigation--previous::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M15.1975 4.46474C15.5885 4.85478 15.5893 5.48794 15.1993 5.87895L8.92124 12.1727L15.1993 18.4665C15.5893 18.8575 15.5885 19.4907 15.1975 19.8807C14.8065 20.2708 14.1734 20.27 13.7833 19.879L6.8008 12.879C6.41145 12.4886 6.41145 11.8568 6.8008 11.4665L13.7833 4.46651C14.1734 4.0755 14.8065 4.0747 15.1975 4.46474Z' fill='%23215369' /%3E%3C/svg%3E");
        }

        &.react-datepicker__navigation--next::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.24214 19.8807C8.85113 19.4907 8.85034 18.8575 9.24038 18.4665L15.5184 12.1727L9.24037 5.87895C8.85034 5.48794 8.85113 4.85478 9.24214 4.46474C9.63315 4.0747 10.2663 4.07549 10.6564 4.46651L17.6389 11.4665C18.0282 11.8568 18.0282 12.4886 17.6389 12.879L10.6564 19.879C10.2663 20.27 9.63316 20.2708 9.24214 19.8807Z' fill='%23215369' /%3E%3C/svg%3E");
        }
      }

      &:focus {
        ${focusStyles}
        &.react-datepicker__navigation--next::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.24214 19.8807C8.85113 19.4907 8.85034 18.8575 9.24038 18.4665L15.5184 12.1727L9.24037 5.87895C8.85034 5.48794 8.85113 4.85478 9.24214 4.46474C9.63315 4.0747 10.2663 4.07549 10.6564 4.46651L17.6389 11.4665C18.0282 11.8568 18.0282 12.4886 17.6389 12.879L10.6564 19.879C10.2663 20.27 9.63316 20.2708 9.24214 19.8807Z' fill='%23163746' /%3E%3C/svg%3E");
        }

        &.react-datepicker__navigation--previous::before {
          background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M15.1975 4.46474C15.5885 4.85478 15.5893 5.48794 15.1993 5.87895L8.92124 12.1727L15.1993 18.4665C15.5893 18.8575 15.5885 19.4907 15.1975 19.8807C14.8065 20.2708 14.1734 20.27 13.7833 19.879L6.8008 12.879C6.41145 12.4886 6.41145 11.8568 6.8008 11.4665L13.7833 4.46651C14.1734 4.0755 14.8065 4.0747 15.1975 4.46474Z' fill='%23163746' /%3E%3C/svg%3E");
        }
      }

      &::before {
        text-align: center;
        display: inline-block;
        content: '';
        height: 32px;
        width: 32px;
        vertical-align: middle;
      }

      &.react-datepicker__navigation--next::before {
        background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M9.24214 19.8807C8.85113 19.4907 8.85034 18.8575 9.24038 18.4665L15.5184 12.1727L9.24037 5.87895C8.85034 5.48794 8.85113 4.85478 9.24214 4.46474C9.63315 4.0747 10.2663 4.07549 10.6564 4.46651L17.6389 11.4665C18.0282 11.8568 18.0282 12.4886 17.6389 12.879L10.6564 19.879C10.2663 20.27 9.63316 20.2708 9.24214 19.8807Z' fill='%23666666' /%3E%3C/svg%3E");
      }

      &.react-datepicker__navigation--previous::before {
        background-image: url("data:image/svg+xml,%3Csvg style='width: 32px; height: 32px;' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fillRule='evenodd' clipRule='evenodd' d='M15.1975 4.46474C15.5885 4.85478 15.5893 5.48794 15.1993 5.87895L8.92124 12.1727L15.1993 18.4665C15.5893 18.8575 15.5885 19.4907 15.1975 19.8807C14.8065 20.2708 14.1734 20.27 13.7833 19.879L6.8008 12.879C6.41145 12.4886 6.41145 11.8568 6.8008 11.4665L13.7833 4.46651C14.1734 4.0755 14.8065 4.0747 15.1975 4.46474Z' fill='%23666666' /%3E%3C/svg%3E");
      }
    }

    .react-datepicker__month-container,
    .react-datepicker__year--container {
      width: 336px;
    }

    .react-datepicker__month {
      margin: 0px;
      background: ${props => COLORS.generateToken({componentType:'bg-surface', defaultVariant:'high'}, props.theme)};
    }

    .react-datepicker__triangle {
      left: 50% !important;
      display: none;
    }

    .react-datepicker-popper[data-placement^='bottom'] {
      margin-top: 0;
    }

    .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
      border-bottom-color: ${props => COLORS.generateToken({componentType:'bg-surface', state:'hover'}, props.theme)};
    }

    .react-datepicker__header {
      border-bottom: 1px solid ${props => COLORS.generateToken({componentType: 'border', defaultVariant: 'subtle'}, props.theme)};
      background: ${props => COLORS.generateToken({componentType: 'bg-surface', defaultVariant: 'subtle'}, props.theme)};
      height: ${(props) => (!props.$yearPicker ? '96px' : '54px')};
      padding-top: 0px;
    }

    .react-datepicker-year-header {
      height: 48px;
      line-height: 48px !important;
      align-items: center;

      ${props => ComponentSStyling(ComponentTextStyle.Bold, COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme))}
    }

    .react-datepicker__current-month {
      height: 48px;
      display: inline-flex;
      align-items: center;
      margin-right: 5px;

      ${props => ComponentSStyling(ComponentTextStyle.Bold, COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme))}
    }

    .react-datepicker__year {
      max-height: 300px;
      overflow-y: scroll;

      ${scrollBarStyling(Size.Small)}
    }

    .react-datepicker__quarter-wrapper{
      width: 100%;
      display: flex;
      align-content: space-evenly;
      flex-wrap: wrap;
      align-items: center;
      gap: 20px;
    }

    .react-datepicker__year-wrapper {
      max-width: 100%;
      justify-content: space-around;
    }

    .react-datepicker__navigation-icon {
      display: none;
    }

    .react-datepicker__day-name {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      height: 48px;
      margin: 0px;
      width: 48px;

      ${props => ComponentMStyling(ComponentTextStyle.Regular, COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme))}
    }

    

    .react-datepicker__year-text {
      background: ${props => COLORS.generateToken({componentType:'bg-surface', defaultVariant:'high'}, props.theme)};
      border: none;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      margin-top: 20px;
      padding-top: 10px;
      padding-bottom: 10px;

      ${props => ComponentMStyling(ComponentTextStyle.Regular, COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme))}
      &.react-datepicker__year-text--disabled {
        display: none;
      }

      &.react-datepicker__day--keyboard-selected {
        color: ${props => COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme)};
        background: ${props => COLORS.generateToken({componentType:'bg-surface', defaultVariant:'high'}, props.theme)};
        font-weight: 400;
        border: none;
      }

      &:hover {
        ${unselectedHoverColoringText}
      }

      &:focus {
        ${focusStyles}
      }

      &.react-datepicker__year-text--selected {
        ${selectedDefaultColoringText}
      }
    }

    .react-datepicker__day, .react-datepicker__quarter-text {
      background: ${props => COLORS.generateToken({componentType:'bg-surface', defaultVariant:'high'}, props.theme)};
      border: none;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      flex-grow: 1;
      height: 40px;
      width: 40px;
      margin: 4px;

      ${props => ComponentMStyling(ComponentTextStyle.Regular, COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme))}
      &.react-datepicker__day--keyboard-selected, .react-datepicker__quarter-text--keyboard-selected {
        color: ${props => COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme)};
        background: ${props => COLORS.generateToken({componentType:'bg-surface', defaultVariant:'high'}, props.theme)};
        flex-grow: 1;
        font-weight: 400;
        border: none;
      }

      &.react-datepicker__quarter-text--selected {
        ${selectedDefaultColoringText}
        flex-grow: 1;
      }

      &:hover {
        ${unselectedHoverColoringText}
      }

      &:focus {
        ${focusStyles}
      }

      &.react-datepicker__day--selected {
        ${selectedDefaultColoringText}
      }

      &.react-datepicker__day--today {
        border-radius: 0.3rem;
        ${todayDefaultColoringText}
        
        &:hover {
          ${unselectedHoverColoringText}
        }

        &:focus {
          ${focusStyles}
        }
      }
    }
  }
`;

const IconWrapper = styled.div`
  pointer-events: none;
  position: absolute;
  left: 16px;
  width: 24px;
  height: 24px;
  z-index: ${+Z_INDEXES.focus + 1};
`;

const DatepickerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledInputFieldStyling = styled(InputFieldStyling)<{disabled?: boolean, readOnly?: boolean}>`
  padding-left: 48px !important;
  
  &::placeholder {
    color: ${props => COLORS.generateToken({componentType: 'text', defaultVariant: 'subtle'}, props.theme)};
  }

  &:focus:not(.focus-visible) {
    box-shadow: inset 0px 0px 0px 2px ${props => COLORS.getColor('primary_300', props.theme)};
  }

  &:hover {
    box-shadow: inset 0px 0px 0px 2px ${props => COLORS.generateToken({componentType: 'border', state: 'hover'}, props.theme)};

    ::placeholder {
      color: ${props => COLORS.generateToken({componentType: 'text', state: 'hover'}, props.theme)};
    }

    cursor: pointer;
    background-color: ${props => COLORS.generateToken({componentType: 'bg-surface', state: 'hover'}, props.theme)};
    color: ${props => COLORS.generateToken({componentType: 'text', state: 'hover'}, props.theme)};
  }

  &:active,
  &.open {
    box-shadow: inset 0px 0px 0px 2px ${props => COLORS.generateToken({componentType: 'border', state: 'active'}, props.theme)};
    background-color: ${props => COLORS.generateToken({componentType: 'bg-surface', state: 'active'}, props.theme)};
    color: ${props => COLORS.generateToken({componentType: 'text', state: 'active'}, props.theme)};

    ::placeholder {
      color: ${props => COLORS.generateToken({componentType: 'text', state: 'active'}, props.theme)};
    }
  }
  
  &:disabled {
    background-color: ${props => COLORS.generateToken({componentType: 'bg-surface', state: 'disabled'}, props.theme)};
  }

  & ~ ${IconWrapper}{
    color: ${props => COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme)};
  }

  &:hover ~ ${IconWrapper} {
    color: ${props => COLORS.generateToken({ componentType: 'text', state: 'hover' }, props.theme)};
  }

  &:active ~ ${IconWrapper}, &.open ~ ${IconWrapper} {
    color: ${props => COLORS.generateToken({ componentType: 'text', state: 'active' }, props.theme)};
  }

  &[tabindex='-1'] ~ ${IconWrapper} {
    color: ${props => !props.$suppressReadOnlyStyles ? COLORS.generateToken({ componentType: 'text', defaultVariant: 'subtle' }, props.theme) :
      COLORS.generateToken({ componentType: 'text', state: 'disabled' }, props.theme)};
  }
`;

const quarterComponentsClassPrefix = 'react-datepicker__quarter-';

const DatepickerField = React.forwardRef<HTMLInputElement, DatepickerFieldProps>(({
                                            disabled,
                                            readOnly,
                                            onChange,
                                            invalid,
                                            value,
                                            validationMessage,
                                            dateFormat,
                                            autoComplete,
                                            placeholder,
                                            required,
                                            yearPicker,
                                            yearsBeforeCurrentDate,
                                            yearsAfterCurrentDate,
                                            margin,
                                            overflowTooltipPosition,
                                            onBlur,
                                            showQuarterYearPicker,
                                            id,
                                            dataTestId,
                                            ...rest
                                          }: DatepickerFieldProps, ref) => {
  // Globally used variables within the component
  const theme = useTheme();
  const inputRef = useFocusVisibleRef();
  const datepickerRef = React.useRef<any>(null);
  const focusedQuarterRef = React.useRef<number>(1);
  const [activeMonthPage, setActiveMonthPage] = React.useState<number>(-1);
  const [yearPickerMode, setYearPickerMode] = React.useState<boolean>(false);
  const [openAt, setOpenAt] = React.useState<Date | null>(null);
  // 'open' flag is used only for tracking current state of the dropdown,
  // for explicitly opening/closing the picker we are using 'datepickerRef.current.setOpen'
  const [open, setOpen] = React.useState<boolean>(false);

  const getFormattedValue = () => {
    if(showQuarterYearPicker)
    {
      const val = dayjs(value);
      return val.year() + ', Q' + val.quarter();
    }
    else 
      return dayjs(value).format(dateFormat ? dateFormat : 'MMMM Do, YYYY');
  }
  /**
   * Format the date in a specific way.
   */
  React.useEffect(() => {
    if (value) inputRef.current.value = getFormattedValue();
  }, [value]);

  function getQuarterItem(classname: string) : HTMLElement {
    return document.getElementsByClassName(quarterComponentsClassPrefix + classname)[0] as HTMLElement;
  }

  React.useEffect(() => {
    if (yearPickerMode) {
      //unfortunately, since there are no refs, and year pickers is rendered not by our code, but by 'react-datepicker'
      //we have to touch DOM directly
      const selectedDate = document.getElementsByClassName('react-datepicker__year-text--selected')[0] as HTMLElement;
      const dropdownParent = selectedDate?.parentElement?.parentElement;
      if (selectedDate && dropdownParent) dropdownParent.scrollTop = selectedDate.offsetTop - 2 * selectedDate.offsetHeight;
    }
  }, [yearPickerMode]);

  React.useEffect(() => {
    if(showQuarterYearPicker && open)
    {
      const wrapper = getQuarterItem('wrapper');
      const handler = (event: KeyboardEvent) => {
        switch(event.key)
        {
          case ' ':
          case 'Enter':
            const currentQ = getQuarterItem(focusedQuarterRef.current + '');
            currentQ.click();
            break;

          case 'ArrowLeft':
            if(focusedQuarterRef.current > 1)
            {
              const currentQ = getQuarterItem(focusedQuarterRef.current + '');
              const previousQ = getQuarterItem((focusedQuarterRef.current - 1) + '');
              if(previousQ)
              {
                currentQ.tabIndex = -1;
                previousQ.tabIndex = 0;
                previousQ.focus();
                focusedQuarterRef.current = focusedQuarterRef.current - 1;
              }
            }
            break;

          case 'ArrowRight':
            if(focusedQuarterRef.current < 4)
            {
              const currentQ = getQuarterItem(focusedQuarterRef.current + '');
              const nextQ = getQuarterItem((focusedQuarterRef.current + 1) + '');
              if(nextQ)
              {
                currentQ.tabIndex = -1;
                nextQ.tabIndex = 0;
                nextQ.focus();
                focusedQuarterRef.current = focusedQuarterRef.current + 1;
              }
            }
            break;
        }
      };

      wrapper.addEventListener('keydown', handler);
      //unfortunately since there are no efs, and quarter picker is rendered not by our code, but by 'react-datepicker'
      //we have to touch DOM directly
      
      const selectedQuarter = getQuarterItem('-selected');
      if(selectedQuarter) {
        for (let i = 1; i < 5; i++) {
          if(selectedQuarter.innerText.includes('Q' + i)) {
            focusedQuarterRef.current = i;
            break;
          }
        }
        selectedQuarter.setAttribute("tabIndex", "0");
      }
      else
      {
        const firstQuarter = getQuarterItem('1');
        if(firstQuarter) {
          focusedQuarterRef.current = 1;
          firstQuarter.setAttribute("tabIndex", "0");
        }
      }

      return () => {
        wrapper.removeEventListener('keydown', handler);
      };
    }
  }, [showQuarterYearPicker, open]);

  //we have to manually reopen the calendar when needed, to guarantee that datepicker will show selected date after year selection
  //otherwise after year selection datepicker will show January month of the selected year
  React.useEffect(() => {
    if (openAt) {
      datepickerRef.current.setOpen(true);
      //setOpenAt(null);
    }
  }, [openAt]);

  React.useImperativeHandle(ref, () => inputRef.current, [inputRef]);

  const handleCalendarClose = () => {
    inputRef.current?.blur();
    setOpen(false);
  };

  const handleCalendarOpen = () => {
    if (openAt) setOpenAt(null);
    setOpen(true);
  };

  let maxDate = new Date(value && new Date(value) > new Date() ? new Date(value) : new Date());
  maxDate.setFullYear(maxDate.getFullYear() + (yearsAfterCurrentDate ?? 50));

  //itemsNumber - controls number of year select options generated in the dropdown
  //items related to dates higher than 'maxDate' will be hidden, thus to avoid creating hundreds of invisible divs, we divide by 5
  let minDate = new Date(value && new Date(value) < new Date() ? new Date(value) : new Date());
  const itemsNumber = Math.round((minDate.getFullYear() - (yearsBeforeCurrentDate ?? 15)) / 5);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Escape' || e.key === 'Esc')
      datepickerRef.current.setOpen(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    // @ts-ignore
    if (!e.currentTarget.contains(e.relatedTarget)) {
      onBlur && onBlur(e);
    }
  };
  const iconSize = 24;

  return (
    <>
      {/* Let's render the input itself */}
      <DatePickerContainer $quarterPicker={showQuarterYearPicker} data-testid={dataTestId} $yearPicker={yearPickerMode} $margin={margin || ''} onBlur={handleBlur}>
        <DatePicker
          ref={datepickerRef}
          onCalendarOpen={handleCalendarOpen}
          onCalendarClose={handleCalendarClose}
          onKeyDown={handleKeyDown}
          id={id}
          onClickOutside={() => datepickerRef.current.setOpen(false)}

          onChange={(e: any) => {
            if (onChange) {
              if (yearPickerMode) {
                const newDate = new Date();
                newDate.setFullYear(e.getFullYear());
                newDate.setMonth(activeMonthPage);
                setOpenAt(newDate);
              } else onChange(e);
            }
            if (yearPickerMode) setYearPickerMode(false);
          }}
          disabled={disabled || readOnly}
          locale={en}
          showQuarterYearPicker={showQuarterYearPicker}
          dateFormat={showQuarterYearPicker ? "yyyy, QQQ" : undefined}
          showYearPicker={yearPickerMode}
          //we manipulate 'yearItemNumber' and 'maxDate' to guarantee number of year select options shown before and after selected year
          yearItemNumber={yearPickerMode ? itemsNumber : undefined}
          maxDate={yearPickerMode ? maxDate : undefined}
          selected={value}
          openToDate={openAt ?? undefined}
          preventOpenOnFocus={true}
          shouldCloseOnSelect={true}
          renderCustomHeader={
            yearPicker
              ? (params: ReactDatePickerCustomHeaderProps) =>
                React.createElement(
                  DatepickerFieldHeader,
                  {
                    ...params,
                    setActiveMonthPage,
                    customHeaderCount: 0,
                    yearPickerMode: yearPickerMode,
                    setYearPickerMode: setYearPickerMode,
                  },
                  null,
                )
              : undefined
          }
          customInput={
            <InputWrapper $disabled={disabled} $readOnly={readOnly}>
              <DatepickerRow>
                  <StyledInputFieldStyling
                    ref={inputRef}
                    type="text"
                    name="datepicker"
                    onKeyDown={(e) => e.key === 'Enter' && datepickerRef.current.setOpen(true)}
                    className={(invalid ? 'invalid' : '' + (open ? ' open' : ''))}
                    tabIndex={disabled || readOnly ? -1 : 0}
                    onFocus={(e) => inputRef.current?.setSelectionRange(0, 0)}
                    autoComplete={autoComplete}
                    placeholder={placeholder}
                    $extraRightPadding={iconSize}
                    disabled={disabled}
                    readOnly={true}
                    $suppressReadOnlyStyles={!readOnly}
                    required={required}
                    {...rest}
                  />
                  <IconWrapper>
                    <SystemIcons.Calendar size="24"/>
                  </IconWrapper>
                </DatepickerRow>
              {!open && <TooltipOverflow position={overflowTooltipPosition} input={inputRef} withArrow={true} maxWidth="100%" size={Size.Small} align='end'>
                {getFormattedValue()}
              </TooltipOverflow>}
            </InputWrapper>
          }
        />
      </DatePickerContainer>

      {/* If there is an error, let's render the error */}
      {validationMessage && (
        <ErrorMessage>
          <SystemIcons.TechnicalWarning color={COLORS.generateToken({ componentType: 'icon', defaultVariant: 'warning' }, theme)}/>
          <span>{validationMessage}</span>
        </ErrorMessage>
      )}
    </>
  );
});

export default DatepickerField;
